import React, { useCallback } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';

import { useNextBankingDayDispatch } from '../../../fetchHooks/useBankingDays';
import type { RecurringPaymentAgreement } from '../../../generated-types';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import useSetReceiver from '../../../hooks/useSetReceiver';
import useShowPaymentResult from '../../../hooks/useShowPaymentResult';
import { SigningContextProvider, useSigningContext } from '../../../signingContext/signingContext';
import { Actions } from '../../../signingContext/signingTypes';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';
import { EikaModal } from '../../EikaModal';
import { SelectedReceiver } from '../SelectedReceiver';
import UpdateRecurringPayment, { ANALYTICS_CATEGORY } from './UpdateRecurringPayment';
import { RecurringPaymentActionType } from './form/actions';
import getInitialRecurringPaymentUpdateState from './form/getInitialRecurringPaymentUpdateState';
import recurringPaymentReducer from './form/reducer';
import useFormContext, { RecurringPaymentFormContext } from './form/useFormContext';
import useValidate from './form/validate';

import './RecurringPaymentDetails.scss';

interface Props {
    recurringPayment: RecurringPaymentAgreement;
    show: boolean;
    onClose: () => void;
    refresh: () => void;
}

const today = getZonedDate();

const RecurringPaymentDetails: React.FC<Props> = ({ recurringPayment, show, onClose, refresh }) => {
    const { handleChange } = useFormContext();

    const { signingOperationPending, signingStatus, signingError, dispatch, showSigningFlow } = useSigningContext();

    useNextBankingDayDispatch(today, handleChange, RecurringPaymentActionType.ONLY_BANKING_DATE);

    const { targetFromAccount } = usePaymentAccountContext();

    const handleSetReceiver = useCallback(
        (receiver: SelectedReceiver) =>
            handleChange({ type: RecurringPaymentActionType.TO_ACCOUNT_SELECTED, payload: receiver }),
        [handleChange],
    );
    useSetReceiver(recurringPayment.toAccount, handleSetReceiver);

    const updateOnChange = useCallback(() => {
        const hasChanged = !!signingError || !!signingStatus || signingOperationPending;
        if (hasChanged && targetFromAccount?.accountId) {
            refresh();
        }
    }, [signingError, signingStatus, signingOperationPending, targetFromAccount?.accountId, refresh]);

    const onCloseUpdate = useCallback(() => {
        updateOnChange();
        dispatch({ type: Actions.RESET });
        onClose();
    }, [updateOnChange, dispatch, onClose]);

    const showPaymentResult = useShowPaymentResult();
    const hideCloseButton = showSigningFlow && !showPaymentResult;

    return (
        <EikaModal
            id="recurringPaymentDetails"
            className="recurring-payment-details-modal"
            hideCloseButton={hideCloseButton}
            open={show}
            setOpen={() => null}
            onClose={onCloseUpdate}
            timeOut={signingStatus === 'COMPLETED' ? 2500 : undefined}
            contentShouldStretch
            size="wide"
            appNamespace="dagligbank-lokalbank-web"
            dataTrackName={paymentTrackIDs.recurring.paymentName}
        >
            <UpdateRecurringPayment
                recurringPayment={recurringPayment}
                paymentOperationPending={signingOperationPending}
                paymentStatus={signingStatus}
                onClose={onClose}
            />
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORY}>
        <RecurringPaymentFormContext
            validateHook={useValidate}
            reducer={recurringPaymentReducer}
            initialState={getInitialRecurringPaymentUpdateState(props.recurringPayment)}
        >
            <RecurringPaymentDetails {...props} />
        </RecurringPaymentFormContext>
    </SigningContextProvider>
);
